import { useReducer, useContext, useEffect, useMemo } from "react"
import { Link } from "react-router"
import { Icon } from "source/shared/components/Icon"
import { useChurchCenterIframe } from "source/shared/hooks/useChurchCenterIframe"
import { useSession } from "source/shared/hooks/useSession"
import { BREAKPOINTS } from "source/shared/hooks/useWindowDimensions"
import { useQRCode } from "source/shared/QRCode"
import { NoActiveFundsBoundary } from "./components/NoActiveFundsBoundary"
import { AmountInput } from "./components/AmountInput"
import { ContinueButton } from "./components/ContinueButton"
import { CreditCardNotAcceptedText } from "./components/CreditCardNotAcceptedAlert"
import { ButtonContainer, FormFooter } from "./components/display/FormFooter"
import { FormSubmit } from "./components/FormSubmit"
import { LeftPane } from "./components/LeftPane"
import { NoRealChargeDisclaimer } from "./components/NoRealChargeDisclaimer"
import { PageNavigation } from "./components/PageNavigation"
import { PaymentSecurityDisclaimer } from "./components/PaymentSecurityDisclaimer"
import { PreviewDonationFormBanner } from "./components/PreviewDonationFormBanner"
import { RightPane } from "./components/RightPane"
import { ConditionalStripeElementsProvider } from "./components/stripe"
import { FormWrapper } from "./components/display/FormWrapper"
import { PaneGroup } from "./components/display/PaneGroup"
import { DonationFormContext } from "./hooks/useDonationFormContext"
import { useMediaQuery } from "source/giving/hooks/useMediaQuery"
import { reducer } from "./stores/reducer"
import { initializeState } from "./stores/initializeState"
import { useCalculatedTotalAndDesignations } from "./hooks/useCalculatedTotalAndDesignations"
import { parsedConfigQuery, useParsedConfig } from "./hooks/useParsedConfig"
import { useValidateProgress } from "./stores/validations"
import { DonationStatus } from "./components/DonationStatus"
import { BrowserNavigationHandler } from "./components/BrowserNavigationHandler"
import { ServerRenderedProps } from "source/shared/contexts/ServerRenderedProps"
import useNotifyNativeLoaded from "source/shared/hooks/useNotifyNativeLoaded"
import "./styles/donation-form.css"
import useProductAnalyticsPublishEvent from "source/shared/hooks/useProductAnalyticsPublishEvent"
import { useIsChurchCenterApp } from "source/Layout"
import { ExpressGive } from "./components/ExpressGive"
import { queryClient } from "source/ReactQueryProvider"
import type { Route } from "./+types/index"

function UnwrappedDonationForm() {
  const config = useParsedConfig()
  const [state, dispatch] = useReducer(reducer, config, initializeState)
  const { inIframe } = useChurchCenterIframe()
  const desktop = useMediaQuery(`(min-width: ${BREAKPOINTS.MD}px)`) && !inIframe
  const {
    meta: { authenticated },
  } = useSession(false)
  const {
    current_organization: { currency },
  } = useContext(ServerRenderedProps)
  const { memo_line_disclaimer: memoDisclaimer = "" } = config

  const {
    focusedPane,
    focusedDesignationIndex,
    designations,
    frequency,
    editFrequencyExpanded,
    errors,
    feeCovered,
    memoChecked,
    memoText,
    formReady,
    stripePaymentMethod,
    savePaymentMethodChecked,
    pollingDonationStatusId,
    campusId,
  } = state

  const { totalCents, calculatedDesignations } =
    useCalculatedTotalAndDesignations(config, state)

  const memoEligible = useMemo(
    () =>
      designations.some((designation) => {
        const fund = config.funds.find((f) => f.id === designation.fundId)
        return fund?.memo_line_eligible === true
      }),
    [designations, config.funds],
  )

  useQRCode()
  useNotifyNativeLoaded()

  const validateProgress = useValidateProgress(state, config, dispatch)

  const twoColumn = desktop && authenticated
  const showAmount = desktop || focusedPane === "left"
  const showLeftPane = twoColumn || focusedPane === "left"
  const showRightPane = twoColumn || focusedPane === "right"
  const showContinue = !twoColumn && focusedPane === "left"
  const showExpressGive =
    config.express_give_eligible && showContinue && !authenticated
  const showSubmit = twoColumn || (!twoColumn && focusedPane === "right")
  const isAppGuestMode = sessionStorage.getItem("app_guest_mode") === "true"
  const isInMobileApp = !desktop && !inIframe
  const showInMobileApp = isInMobileApp && authenticated && !isAppGuestMode
  const showInEmbeddedModal = inIframe && !authenticated
  const showMyGivingLink =
    showLeftPane && (showInEmbeddedModal || showInMobileApp)
  const isCCA = useIsChurchCenterApp()
  const publishEvent = useProductAnalyticsPublishEvent()

  useEffect(() => {
    publishEvent("giving.donation-form.loaded", {
      source: isCCA ? "CCA" : desktop ? "CCW Desktop" : "CCW Mobile",
    })
  }, [])

  return (
    <DonationFormContext.Provider value={{ config, dispatch }}>
      <ConditionalStripeElementsProvider
        condition={authenticated}
        {...{
          amountCents: totalCents,
          currency: currency.toLowerCase(),
          frequency,
          stripePaymentMethod,
          savePaymentMethodChecked,
        }}
      >
        {!desktop && (
          <BrowserNavigationHandler {...{ dispatch, focusedPane }} />
        )}
        {desktop && authenticated && <PageNavigation />}
        <FormWrapper authenticated={authenticated}>
          <PreviewDonationFormBanner />
          {showAmount && <AmountInput {...{ designations, errors }} />}
          <PaneGroup>
            {showLeftPane && (
              <LeftPane
                {...{
                  designations,
                  frequency,
                  editFrequencyExpanded,
                  errors,
                  focusedDesignationIndex,
                  campusId,
                  memo: {
                    checked: memoChecked,
                    disclaimer: memoDisclaimer,
                    eligible: memoEligible,
                    text: memoText,
                  },
                }}
              />
            )}
            {showRightPane && (
              <RightPane
                {...{
                  designations,
                  desktop,
                  totalCents,
                  feeCovered,
                  frequency,
                  stripePaymentMethod,
                  savePaymentMethodChecked,
                }}
              />
            )}
          </PaneGroup>
          <FormFooter>
            {(showExpressGive || showContinue) && (
              <ButtonContainer>
                {showExpressGive && (
                  <>
                    <CreditCardNotAcceptedText />
                    <ExpressGive
                      {...{
                        state,
                        memoEligible,
                        totalCents,
                        validateProgress,
                        desktop,
                      }}
                    />
                  </>
                )}
                {showContinue && (
                  <ContinueButton
                    showExpressGive={
                      showExpressGive && state.expressGiveStatus === "ready"
                    }
                    {...{
                      authenticated,
                      validateProgress,
                      desktop,
                    }}
                  />
                )}
              </ButtonContainer>
            )}
            {showMyGivingLink && (
              <Link
                to="/giving/profile"
                className="btn text-btn"
                {...(inIframe
                  ? { target: "_blank", rel: "noopener noreferrer" }
                  : {})}
              >
                View my giving
                <span className="p-r ml-1 c-tint3" css={{ top: "2px" }}>
                  <Icon symbol="general#right-arrow" />
                </span>
              </Link>
            )}
            {pollingDonationStatusId && (
              <DonationStatus
                id={pollingDonationStatusId}
                expressFirstName={state.expressFirstName}
              />
            )}
            {showSubmit && (
              <>
                <ButtonContainer>
                  <NoRealChargeDisclaimer />
                  <FormSubmit
                    {...{
                      state,
                      totalCents,
                      calculatedDesignations,
                      validateProgress,
                      disabled: !formReady,
                      desktop,
                      memoEligible,
                    }}
                  />
                </ButtonContainer>
                <PaymentSecurityDisclaimer />
              </>
            )}
          </FormFooter>
        </FormWrapper>
      </ConditionalStripeElementsProvider>
    </DonationFormContext.Provider>
  )
}

export async function clientLoader({ params, request }: Route.LoaderArgs) {
  const { fundSlug } = params
  const requestUrl = new URL(request.url)
  const queryString = requestUrl.search
  const configQuery = parsedConfigQuery(queryString, fundSlug)

  return await queryClient.ensureQueryData(configQuery)
}

export default function DonationForm() {
  return (
    <NoActiveFundsBoundary>
      <UnwrappedDonationForm />
    </NoActiveFundsBoundary>
  )
}
